import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell, AlertTriangle, UserPlus, Ticket, ShieldCheck } from 'lucide-react';
import { api } from '../../services/api';

interface Notification {
  id: string;
  type: 'alert' | 'signup' | 'ticket' | 'admin';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  timestamp: string;
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  alert: AlertTriangle,
  signup: UserPlus,
  ticket: Ticket,
  admin: ShieldCheck,
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState<number>(() => {
    const stored = localStorage.getItem('lumiadmin-notifications-seen');
    return stored ? parseInt(stored) : 0;
  });
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery<{ notifications: Notification[]; count: number }>({
    queryKey: ['admin-notifications'],
    queryFn: () => api.get('/api/admin/notifications'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const notifications = data?.notifications || [];
  const unseen = notifications.filter((n) => new Date(n.timestamp).getTime() > seenAt).length;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const now = Date.now();
      setSeenAt(now);
      try {
        localStorage.setItem('lumiadmin-notifications-seen', String(now));
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unseen > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <span className="text-xs text-gray-400">{notifications.length} recent</span>
          </div>
          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            {notifications.length === 0 ? (
              <p className="px-4 py-8 text-sm text-gray-400 text-center">No notifications</p>
            ) : (
              notifications.map((n) => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                return (
                  <div
                    key={n.id}
                    className="px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 flex gap-3"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        n.severity === 'critical'
                          ? 'bg-red-100'
                          : n.severity === 'warning'
                          ? 'bg-yellow-100'
                          : 'bg-blue-100'
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${
                          n.severity === 'critical'
                            ? 'text-red-600'
                            : n.severity === 'warning'
                            ? 'text-yellow-700'
                            : 'text-blue-800'
                        }`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 capitalize">{n.title}</p>
                      {n.message && <p className="text-xs text-gray-500 truncate">{n.message}</p>}
                      <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(n.timestamp)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
